import { UseQueryResult } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import { debounce } from "lodash";
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import {
  GetReportsQuery,
  PassageGoalType,
  ProgramReviewGoalsQuery,
  ReportTypeEnums,
  useGetReportsQuery,
  useProgramReviewGoalsQuery,
} from "@api/graphql/types-and-hooks";

// Program review types
type ProgramReviewGoalNode = NonNullable<
  NonNullable<NonNullable<ProgramReviewGoalsQuery["programReviewGoals"]>["edges"][number]>["node"]
>;
type ProgramReviewTarget = NonNullable<
  NonNullable<NonNullable<ProgramReviewGoalNode["passageTargets"]>["edges"][number]>["node"]
>;
export type ProgramReviewGoal = ProgramReviewGoalNode;
export type ProgramReviewGoalList = Array<ProgramReviewGoal> | null | undefined;

interface ProgrammingReviewContextI {
  getProgramReviewGoalsQuery: UseQueryResult<ProgramReviewGoalsQuery, unknown>;
  programReviewGoalList: ProgramReviewGoalList;
  selectedProgramReviewGoal: ProgramReviewGoal | null;
  selectedProgramReviewTarget: ProgramReviewTarget | null;
  setSelectedProgramReviewGoal: (goal: ProgramReviewGoal | null) => void;
  setSelectedProgramReviewTarget: (target: ProgramReviewTarget | null) => void;
  learnerId: string | undefined;
  searchGoalText: string;
  setSearchGoalText: (value: string | ((prev: string) => string)) => void;
  latestInitialAssessmentReportQuery: UseQueryResult<GetReportsQuery, unknown>;
}

const ProgrammingReviewContext = createContext<ProgrammingReviewContextI | undefined>(undefined);

export const useProgrammingReviewData = () => {
  const context = useContext(ProgrammingReviewContext);
  if (context === undefined) {
    throw new Error("useProgrammingReviewData must be used within a ProgrammingReviewProvider");
  }
  return context;
};

export const ProgrammingReviewProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { clientId, goalId, targetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isReviewRoute = location.pathname.includes("/programming/review/");

  const [selectedGoalId, setSelectedGoalIdInternal] = useState<string | null>(null);
  const [selectedTargetId, setSelectedTargetIdInternal] = useState<string | null>(null);

  // Report query
  const latestInitialAssessmentReportQuery = useGetReportsQuery(
    {
      first: 1,
      filter: {
        learnerId: clientId || "",
        reportType: ReportTypeEnums.InitialAssessment,
        sortBy: "-created_date",
      },
    },
    {
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  // Search functionality
  const searchGoalText = searchParams.get("search") || "";
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const debouncedUpdateSearchParams = useCallback(
    debounce((value: string) => {
      if (value.trim()) {
        setSearchParams({ search: value }, { replace: true });
      } else {
        const newSearchParams = new URLSearchParams(searchParamsRef.current);
        newSearchParams.delete("search");
        setSearchParams(newSearchParams, { replace: true });
      }
    }, 100),
    [setSearchParams],
  );

  const setSearchGoalText = (value: string | ((prev: string) => string)) => {
    const newValue = typeof value === "function" ? value(searchGoalText) : value;
    debouncedUpdateSearchParams(newValue);
  };

  // Program review query - only fetch on review routes
  const getProgramReviewGoalsQuery = useProgramReviewGoalsQuery(
    { learnerId: clientId! },
    {
      enabled: !!clientId && isReviewRoute,
      queryKey: ["program-review-goals", clientId],
    },
  );

  const { data: programReviewGoalsData } = getProgramReviewGoalsQuery;

  // Process and sort goals by rankOrder
  const programReviewGoalList: ProgramReviewGoalList = useMemo(() => {
    const goals = unwrapConnection(programReviewGoalsData?.programReviewGoals) as PassageGoalType[] | undefined;
    return goals?.slice().sort((a, b) => (a.goal?.rankOrder ?? 0) - (b.goal?.rankOrder ?? 0));
  }, [programReviewGoalsData]);

  // Build lookup tables
  const [programReviewGoalById, programReviewTargetById, programReviewGoalIdByTargetId] = useMemo(() => {
    const goalById: Record<string, ProgramReviewGoal> = {};
    const targetById: Record<string, ProgramReviewTarget> = {};
    const goalIdByTargetId: Record<string, string> = {};

    for (const goal of programReviewGoalList || []) {
      goalById[goal.id] = goal;
      const targets = unwrapConnection(goal.passageTargets);
      for (const target of targets || []) {
        targetById[target.id] = target;
        goalIdByTargetId[target.id] = goal.id;
      }
    }
    return [goalById, targetById, goalIdByTargetId];
  }, [programReviewGoalList]);

  // Track if we've done initial URL sync
  const hasInitializedFromUrl = useRef(false);

  // Sync URL params with state - only on initial load
  useEffect(() => {
    if (hasInitializedFromUrl.current) return;

    if (programReviewGoalList?.length) {
      hasInitializedFromUrl.current = true;

      if (goalId) {
        const goal = programReviewGoalList.find((g) => g.id === goalId);
        if (goal) {
          if (targetId) {
            const target = goal.passageTargets?.edges.find((t) => t?.node?.id === targetId);
            if (target) {
              setSelectedTargetIdInternal(targetId);
              setSelectedGoalIdInternal(goalId);
            }
          } else {
            setSelectedGoalIdInternal(goalId);
          }
        }
      } else {
        // Auto-select first goal if no goalId in URL
        const firstGoal = programReviewGoalList[0];
        if (firstGoal) {
          setSelectedGoalIdInternal(firstGoal.id);
        }
      }
    }
  }, [goalId, targetId, programReviewGoalList]);

  // Derived selected values
  const selectedProgramReviewGoal = selectedGoalId ? programReviewGoalById[selectedGoalId] : null;
  const selectedProgramReviewTarget = selectedTargetId ? programReviewTargetById[selectedTargetId] : null;

  // Setter functions
  const setSelectedProgramReviewGoal = useCallback((goal: ProgramReviewGoal | null) => {
    if (goal) {
      setSelectedGoalIdInternal(goal.id);
      setSelectedTargetIdInternal(null);
    } else {
      setSelectedGoalIdInternal(null);
      setSelectedTargetIdInternal(null);
    }
  }, []);

  const setSelectedProgramReviewTarget = useCallback(
    (target: ProgramReviewTarget | null) => {
      if (target) {
        setSelectedTargetIdInternal(target.id);
        const parentGoalId = programReviewGoalIdByTargetId[target.id];
        if (parentGoalId) {
          setSelectedGoalIdInternal(parentGoalId);
        }
      } else {
        setSelectedTargetIdInternal(null);
      }
    },
    [programReviewGoalIdByTargetId],
  );

  return (
    <ProgrammingReviewContext.Provider
      value={{
        latestInitialAssessmentReportQuery,
        getProgramReviewGoalsQuery,
        programReviewGoalList,
        selectedProgramReviewGoal,
        selectedProgramReviewTarget,
        setSelectedProgramReviewGoal,
        setSelectedProgramReviewTarget,
        learnerId: clientId,
        searchGoalText,
        setSearchGoalText,
      }}
    >
      {children}
    </ProgrammingReviewContext.Provider>
  );
};
